// src/pages/Utilisateurs/PrepaPhysique/PrepaPhysiqueList.jsx
import React, { useEffect, useState } from "react";
import { updateAdherentExerciseResult } from "../../../services/prepaPhysiqueServices";
import { toast } from "react-toastify";
import { FaExternalLinkAlt, FaYoutube } from "react-icons/fa";

/** Switch accessible pour marquer l'exercice comme (non) effectué */
function DoneSwitch({ checked, onChange, disabled, title }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={!!checked}
      aria-label={
        title || (checked ? "Exercice effectué" : "Exercice non effectué")
      }
      title={title || (checked ? "Exercice effectué" : "Exercice non effectué")}
      disabled={disabled}
      onClick={onChange}
      className={`relative inline-flex h-6 w-12 items-center rounded-full transition
        ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}
        ${checked ? "bg-emerald-500" : "bg-slate-300 dark:bg-slate-700"}`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition
          ${checked ? "translate-x-6" : "translate-x-1"}`}
      />
    </button>
  );
}

function formatFR(d) {
  if (!d) return "—";
  const dt = new Date(d);
  if (Number.isNaN(dt.valueOf())) return "—";
  return dt.toLocaleDateString("fr-FR");
}

function PrepaPhysiqueList({ exercices, adherentId }) {
  const [localExercices, setLocalExercices] = useState([]);
  const [savingIds, setSavingIds] = useState({}); // { [exId]: true }
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    if (Array.isArray(exercices)) setLocalExercices(exercices);
  }, [exercices]);

  if (!Array.isArray(localExercices) || localExercices.length === 0) {
    return (
      <p className="text-sm text-slate-600 dark:text-slate-300">
        Aucun exercice attribué.
      </p>
    );
  }

  const handleToggleIsDone = async (ex, index) => {
    const ap = ex.AdherentPrepaPhysique || {};
    const currentStatus = !!ap.is_done;
    const newStatus = !currentStatus;

    if (savingIds[ex.id]) return; // anti double-clic
    setSavingIds((m) => ({ ...m, [ex.id]: true }));

    const dateReal =
      ap.date_realisation && ap.date_realisation !== "0000-00-00"
        ? ap.date_realisation
        : new Date().toISOString().slice(0, 10);

    const payload = {
      adherent_id: adherentId,
      prepa_physique_id: ex.id,
      date_realisation: dateReal,
      repetitions: ap.repetitions || 0,
      is_done: newStatus,
    };

    try {
      const res = await updateAdherentExerciseResult(payload);
      if (res?.success) {
        toast.success("État mis à jour !");
        setLocalExercices((prev) => {
          const updated = [...prev];
          const next = {
            ...(updated[index] || {}),
            AdherentPrepaPhysique: {
              ...(updated[index]?.AdherentPrepaPhysique || {}),
              is_done: newStatus,
              date_realisation: dateReal,
            },
          };
          updated[index] = next;
          return updated;
        });
      } else {
        toast.error("Erreur lors de la mise à jour.");
      }
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de la mise à jour.");
    } finally {
      setSavingIds((m) => ({ ...m, [ex.id]: false }));
    }
  };

  const openImageModal = (url) => setSelectedImage(url);
  const closeImageModal = () => setSelectedImage(null); // clic n'importe où -> fermer

  return (
    <div className="space-y-4">
      {localExercices.map((ex, index) => {
        const ap = ex.AdherentPrepaPhysique || {};
        const done = !!ap.is_done;
        return (
          <article
            key={ex.id}
            className="rounded-2xl overflow-hidden ring-1 ring-slate-200 dark:ring-slate-800 bg-white/90 dark:bg-slate-900/60"
          >
            <div className="grid md:grid-cols-3 gap-0">
              {/* Image / illustration (pas de hover) */}
              {/* Image / illustration (pas de hover, juste ouverture modale) */}
              <div
                role="button"
                tabIndex={0}
                onClick={() =>
                  openImageModal(ex.lien_photo || "/img/no-image.jpg")
                }
                onKeyDown={(e) =>
                  (e.key === "Enter" || e.key === " ") &&
                  openImageModal(ex.lien_photo || "/img/no-image.jpg")
                }
                className="md:col-span-1 cursor-zoom-in bg-slate-50/70 dark:bg-slate-900/70 border-r border-slate-200 dark:border-slate-800 select-none"
                title="Agrandir l’illustration"
              >
                <img
                  src={ex.lien_photo || "/img/no-image.jpg"}
                  alt={ex.nom || "Illustration"}
                  draggable={false}
                  className="block w-full h-full max-h-[240px] md:max-h-full object-cover"
                />
              </div>

              {/* Contenu */}
              <div className="md:col-span-2 p-4 sm:p-5 ml-4">
                <header className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h4 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100 truncate">
                      {ex.nom || "Exercice"}
                    </h4>
                    {ex.programme && (
                      <div className="mt-1 inline-flex items-center gap-2 text-xs px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 ring-1 ring-slate-200/70 dark:ring-slate-700/70">
                        Programme :{" "}
                        <span className="font-medium">{ex.programme}</span>
                      </div>
                    )}
                  </div>

                  {/* Switch validation */}
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="hidden sm:inline text-xs opacity-70">
                      {done ? "Effectué" : "Non effectué"}
                    </span>
                    <DoneSwitch
                      checked={done}
                      disabled={!!savingIds[ex.id]}
                      onChange={() => handleToggleIsDone(ex, index)}
                      title={
                        done
                          ? "Basculer en non effectué"
                          : "Basculer en effectué"
                      }
                    />
                  </div>
                </header>

                {/* Description enrichie */}
                {ex.description && (
                  <div
                    className="mt-3 prose prose-sm dark:prose-invert max-w-none text-slate-800 dark:text-slate-200"
                    dangerouslySetInnerHTML={{ __html: ex.description }}
                  />
                )}

                {/* Métadonnées + carte vidéo */}
                <div className="mt-3 flex flex-wrap items-center gap-2 text-xs pointer-events-none">
                  {ap.repetitions != null && (
                    <span className="px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 ring-1 ring-slate-200/70 dark:ring-slate-700/70">
                      Répétitions : <strong>{ap.repetitions}</strong>
                    </span>
                  )}
                  {ap.date_realisation &&
                    ap.date_realisation !== "0000-00-00" && (
                      <span className="px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 ring-1 ring-slate-200/70 dark:ring-slate-700/70">
                        Date : <strong>{formatFR(ap.date_realisation)}</strong>
                      </span>
                    )}

                  {ex.lien_video && (
                    <a
                      href={ex.lien_video}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      className="pointer-events-auto relative z-10 inline-flex items-center gap-3 px-3 py-2 rounded-xl ring-1 ring-slate-200 dark:ring-slate-700 bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800 transition text-slate-900 dark:text-slate-100"
                      title="Ouvrir la vidéo"
                    >
                      <FaYoutube className="text-red-600 text-5xl" />
                      <span className="text-xs sm:text-sm font-medium">
                        Voir la vidéo
                      </span>
                      <FaExternalLinkAlt className="opacity-70" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          </article>
        );
      })}

      {/* Petite modale interne pour l’image — clic n'importe où pour fermer */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={closeImageModal} // clic n’importe où -> fermer
        >
          <div className="max-w-2xl w-full rounded-2xl overflow-hidden ring-1 ring-white/40 bg-black/20 p-2">
            <img
              src={selectedImage}
              alt="Agrandissement"
              className="block w-full max-h-[65vh] object-contain rounded-xl"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default PrepaPhysiqueList;
